import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { SITE } from "@/lib/site";
import { GBP_CATEGORIES } from "@/lib/reference/categories";
import { GBP_ATTRIBUTES } from "@/lib/reference/attributes";
import { JUSTIFICATIONS } from "@/lib/reference/justifications";
import { SUSPENSIONS } from "@/lib/reference/suspensions";

export const Route = createFileRoute("/tools/")({
  head: () => {
    const url = `${SITE.url}/tools`;
    return {
      meta: [
        { title: `Local SEO Tools & References — GBP Categories, Attributes, Justifications | ${SITE.name}` },
        {
          name: "description",
          content:
            "Free reference tools for local SEO and Google Business Profile operators: searchable category finder, attribute database, justification decoder, suspension lookup, and LocalBusiness schema generator.",
        },
        { property: "og:title", content: `Local SEO Tools | ${SITE.name}` },
        { property: "og:description", content: "Reference tools for GBP categories, attributes, justifications and schema." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Tools", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ToolsHub,
});

const TOOLS = [
  {
    to: "/tools/gbp-categories",
    title: "GBP Category Finder",
    blurb: "Search Google Business Profile categories, see what attributes each unlocks, and avoid the wrong-primary trap.",
    count: `${GBP_CATEGORIES.length} categories indexed`,
  },
  {
    to: "/tools/gbp-attributes",
    title: "GBP Attributes Database",
    blurb: "Every attribute a merchant can set — what it means, who can set it, how it surfaces in Maps.",
    count: `${GBP_ATTRIBUTES.length} attributes catalogued`,
  },
  {
    to: "/tools/justifications",
    title: "Justification Decoder",
    blurb: "The snippets Google shows under your Map Pack listing — what each one is and how to earn it.",
    count: `${JUSTIFICATIONS.length} justification types`,
  },
  {
    to: "/tools/suspensions",
    title: "Suspension Reason Lookup",
    blurb: "Why GBP listings get suspended, what to do about it, and how to prevent it next time.",
    count: `${SUSPENSIONS.length} reasons documented`,
  },
  {
    to: "/tools/local-schema",
    title: "Local Schema Generator",
    blurb: "Generate valid LocalBusiness JSON-LD with NAP, hours, geo, and sameAs — copy-paste ready.",
    count: "Free, client-side, no signup",
  },
] as const;

function ToolsHub() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li>Tools</li>
          </ol>
        </nav>
        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Reference</p>
          <h1 className="headline-display mt-3 text-5xl">Local SEO tools & references</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Free, citable resources for agencies and business owners working on Google Business Profile, the Map Pack, and local search. Every page has a permalink — link, embed, or share.
          </p>
        </header>

        <ul className="mt-12 grid gap-6 sm:grid-cols-2">
          {TOOLS.map((t) => (
            <li key={t.to}>
              <Link
                to={t.to}
                className="block h-full rounded-sm border border-border bg-paper-soft p-6 hover:border-foreground hover:bg-background transition-colors"
              >
                <p className="eyebrow">{t.count}</p>
                <h2 className="mt-2 headline-display text-2xl">{t.title}</h2>
                <p className="mt-3 text-sm text-foreground/80">{t.blurb}</p>
                <span className="mt-4 inline-block text-xs uppercase tracking-wider text-ochre-deep">Open →</span>
              </Link>
            </li>
          ))}
        </ul>

        <NewsletterCTA />
      </main>
      <SiteFooter />
    </div>
  );
}
